import { isObject } from 'lodash';
import React, { Component } from 'react';
import Data from '../../data';
import Layout from '../../layout';
import Modal from '../../modal';
import UI from '../../ui';
import Utils from '../../utils';
import AddCarrierHeader from '../AddCarrierHeader.component';

const { Util, Validation, Session } = Utils;
const { ModalContext } = Modal;
const { LayoutContext } = Layout;
const { Carrier } = Data;
const {
	WrappedInputTextWithLabel,
	WrappedRadioButton,
	WrappedCheckbox,
	Footer,
	Section,
	SectionBody,
	Spinner,
} = UI;

export default class AddUpsAccount extends Component {
	constructor(props) {
		super(props);

		const { coreData = {} } = this.props;
		this.state = {
			upsAccountZip: coreData.upsAccountZip || '',
			upsAccountNumber: coreData.upsAccountNumber || '',
			negotiatedRates: coreData.negotiatedRates || true,
			validationErrors: {
				upsAccountNumberError: coreData.AccountNumber ? 1 : 0,
				upsAccountZipError: coreData.upsAccountZip ? 1 : 0,
			},
		};
	}

	handleInputChange = e => {
		const {
			target,
			target: { name },
		} = e;
		const value = target.type === 'checkbox' ? target.checked : target.value;

		if (name === 'upsAccountZip') {
			const formattedZip = Util.formatZip(value);
			this.setState({ [name]: formattedZip });
		} else {
			this.setState({ [name]: value });
		}
	};

	handleRadioButtonChange = () => {
		const { handleRadioButton } = this.props;

		if (handleRadioButton) {
			handleRadioButton();
		}
	};

	// input field validation
	onInputTextBlur = e => {
		const { name } = e.target;
		const errorName = `${name}Error`;
		const { [name]: fieldValue } = this.state;
		const minCharCount = Number(e.target.getAttribute('mincharcount'));

		this.setState(prevState => ({
			...prevState,
			validationErrors: {
				...prevState.validationErrors,
				[errorName]:
					fieldValue !== '' && fieldValue.length >= minCharCount ? 1 : -1,
			},
		}));
	};

	onContinueButtonClick = async () => {
		const { increasePageCount, userOwnedAccount } = this.props;

		if (!userOwnedAccount) {
			increasePageCount();
		}
	};

	validateCoreAccount = () => {
		const { validationErrors } = this.state;
		const newValidations = validationErrors;
		let validated = true;

		// changes untouched required input fields to have an error
		Object.keys(newValidations).map(key => {
			if (newValidations[key] === 0) {
				newValidations[key] = -1;
			}

			return null;
		});

		this.setState({
			validationErrors: newValidations,
		});

		Object.keys(validationErrors).forEach(validationError => {
			if (validationErrors[validationError] === -1) {
				validated = false;
			} else {
				validated = true;
			}
		});

		return validated;
	};

	onCancelButtonClick = () => {
		const { carrierCallback } = this.props;
		carrierCallback(false);
	};

	authenticateWithUPS = async () => {
		const { initiateMonoauth, editCarrierDetails } = this.props;
		const { upsAccountNumber, upsAccountZip, negotiatedRates } = this.state;

		// Never trust the query string or a foreign referrer: only a same-origin referrer is
		// used (it carries the host page path); otherwise fall back to our own origin.
		const referrer = document.referrer;
		const redirectUrl =
			referrer &&
			new URL(referrer, window.location.href).origin === window.location.origin
				? referrer
				: window.location.origin;

		const data = {
			Carrier: 'ups',
			MonoauthType: 'ups',
			ZipCode: upsAccountZip,
			CountryCode: 'US',
			AccountNumber: upsAccountNumber || editCarrierDetails?.accountNumber,
			RedirectUrl: redirectUrl,
			NegotiatedRates: negotiatedRates,
		};

		if (this.validateCoreAccount()) {
			initiateMonoauth(data);
		}
	};

	renderAddView = isMobile => {
		const {
			upsAccountNumber,
			upsAccountZip,
			negotiatedRates,
			validationErrors: { upsAccountNumberError, upsAccountZipError },
		} = this.state;
		const {
			isLoading,
			communicationError,
			formatTradeMark,
			userOwnedAccount,
			enterpriseLocations,
			isSingleLocationSelected,
		} = this.props;

		const mobileClassName = isMobile ? 'mobile-wrapper' : '';
		return (
			<div className="common-carrier-add-ups-account">
				<Section>
					<AddCarrierHeader
						title="Configure Account"
						carrier={Carrier.UPS}
						subtitle={formatTradeMark('subtitle', isMobile)}
						isMobile={isMobile}
					/>
					<SectionBody classNames="ups-section-body">
						{isMobile && formatTradeMark('subtitle', isMobile)}
						<div className="radio-container">
							<WrappedRadioButton
								inputId="sasRadio"
								name="sas"
								value="sas"
								onChange={this.handleRadioButtonChange}
								checked={!userOwnedAccount}
								dataqa="sasradio"
							>
								I want to open a&nbsp;
								<b>UPS Preferred Merchant Account</b> with discounts of up to
								88% and other perks.
							</WrappedRadioButton>
							<WrappedRadioButton
								inputId="coreRadio"
								name="core"
								value="core"
								onChange={this.handleRadioButtonChange}
								checked={userOwnedAccount}
								dataqa="coreradio"
								disabled={enterpriseLocations || isSingleLocationSelected}
							>
								I want to use my existing account and rates.
							</WrappedRadioButton>
						</div>

						{enterpriseLocations && (
							<div className="note-container">
								<i>
									Not available during bulk update. To use existing UPS account
									and rates, <strong>Exit</strong> this bulk update process and
									set up each location individually.
								</i>
							</div>
						)}
						{isSingleLocationSelected && (
							<div className="note-container">
								<i>
									Not available to add via Location Settings. To use existing
									UPS account and rates, <strong>Exit</strong> this bulk update
									process and advise individual Location Administrators to add
									UPS.
								</i>
							</div>
						)}
						{userOwnedAccount && (
							<div className="auth-ups-container">
								<p className="title">UPS Account Authentication</p>
								<p className="content">
									Enter your UPS account number below and click{' '}
									<b>Authenticate with UPS</b> to bring your UPS
									<br />
									account and rates into Stamps.com
								</p>
								<div className="p-col-12 p-md-10">
									<WrappedInputTextWithLabel
										showErrorBelow={isMobile}
										isInputInvalid={upsAccountNumberError === -1}
										errorMessage="This field is required"
										className={`inputtext ${mobileClassName}`}
										label="UPS Account Number:"
										uiElementConfig={{
											type: 'text',
											value: upsAccountNumber,
											name: 'upsAccountNumber',
											onChange: this.handleInputChange,
											onBlur: this.onInputTextBlur,
											mincharcount: 6,
											required: true,
											maxLength: 6,
											dataqa: 'upsaccountnumber',
										}}
									/>
									<WrappedInputTextWithLabel
										showErrorBelow={isMobile}
										isInputInvalid={upsAccountZipError === -1}
										errorMessage="This field is required"
										className={`inputtext ${mobileClassName}`}
										label="ZIP Code for UPS Account Number:"
										uiElementConfig={{
											type: 'text',
											value: upsAccountZip,
											name: 'upsAccountZip',
											onChange: this.handleInputChange,
											onBlur: this.onInputTextBlur,
											mincharcount: 5,
											onKeyPress: Validation.acceptOnlyNumbersInput,
											chartoaccept: 45,
											required: true,
											maxLength: 10,
											dataqa: 'upsaccountzip',
										}}
									/>
									<WrappedCheckbox
										inputId="cb2"
										name="negotiatedRates"
										value={negotiatedRates}
										onChange={this.handleInputChange}
										checked={negotiatedRates}
										htmlFor="cb2"
										dataqa="negotiatedrates"
									>
										Display negotiated rates
									</WrappedCheckbox>
								</div>
								{isLoading && <Spinner msg="Connecting UPS Account..." />}
								{isMobile && formatTradeMark('footer', isMobile)}
							</div>
						)}
						{isMobile && !userOwnedAccount
							? formatTradeMark('footer', isMobile)
							: null}
					</SectionBody>
					<Footer
						primary={
							userOwnedAccount
								? {
										label: 'Authenticate with UPS',
										onClick: this.authenticateWithUPS,
										disabled: isLoading && communicationError,
										dataqa: 'add-ups-account-authenticate-with-ups',
									}
								: {
										label: 'Continue',
										onClick: this.onContinueButtonClick,
										disabled: isLoading && communicationError,
										dataqa: 'add-ups-account-continue',
									}
						}
						secondary={{
							label: enterpriseLocations ? 'Start Over' : 'Cancel',
							onClick: this.onCancelButtonClick,
							dataqa: `add-ups-account-${
								enterpriseLocations ? 'start-over' : 'cancel'
							}`,
						}}
					>
						{!isMobile && formatTradeMark('footer', isMobile)}
					</Footer>
					{isLoading && communicationError && (
						<Spinner msg="Connecting UPS Account..." />
					)}
				</Section>
			</div>
		);
	};

	renderEditView = isMobile => {
		const {
			upsAccountZip,
			validationErrors: { upsAccountZipError },
		} = this.state;
		const { isLoading, editCarrierDetails } = this.props;
		const mobileClassName = isMobile ? 'mobile-wrapper' : '';

		return (
			<div className="common-carrier-add-ups-account">
				<Section>
					<AddCarrierHeader
						title="Edit UPS Account"
						className="edit-view-header"
						carrier={Carrier.UPS}
						accountNumber={editCarrierDetails?.accountNumber}
						isMobile={isMobile}
					/>
					<hr />
					<SectionBody>
						<div className="auth-ups-container">
							<p className="title">Reauthenticate UPS Account</p>
							<p className="content">
								UPS requires reauthentication after certain settings have been
								changed or a certain amount of time has passed.
							</p>
							<div className="p-col-12 p-md-8">
								<WrappedInputTextWithLabel
									showErrorBelow={isMobile}
									isInputInvalid={upsAccountZipError === -1}
									errorMessage="This field is required"
									className={`inputtext ${mobileClassName}`}
									label="ZIP Code for UPS Account Number:"
									uiElementConfig={{
										type: 'text',
										value: upsAccountZip,
										name: 'upsAccountZip',
										onChange: this.handleInputChange,
										onBlur: this.onInputTextBlur,
										mincharcount: 5,
										onKeyPress: Validation.acceptOnlyNumbersInput,
										chartoaccept: 45,
										required: true,
										maxLength: 10,
										dataqa: 'upsaccountzip',
									}}
								/>
							</div>
						</div>
					</SectionBody>
					<Footer
						primary={{
							label: 'Authenticate with UPS',
							onClick: this.authenticateWithUPS,
							disabled: isLoading,
							dataqa: 'add-ups-account-authenticate-with-ups',
						}}
					/>

					{isLoading && <Spinner msg="Connecting UPS Account..." />}
				</Section>
			</div>
		);
	};

	render() {
		const { action, editCarrierDetails } = this.props;

		return (
			<LayoutContext.Consumer>
				{layoutContext => {
					const { isMobile } = layoutContext;

					if (action === 'edit' && isObject(editCarrierDetails)) {
						return this.renderEditView(isMobile);
					} else {
						return this.renderAddView(isMobile);
					}
				}}
			</LayoutContext.Consumer>
		);
	}
}

AddUpsAccount.contextType = ModalContext;
